import React from "react";
import { TeamBadge } from "../TeamBadge";
import { teamColor, TEAM_INFO } from "../../lib/teamMap";
import { assetUrl } from "../../lib/api";
import { usePlayerCrop } from "../PlayerCropContext";

// ── TEAM STYLE V2 ─────────────────────────────────────────────────────────────
// A DISTINCT contract-card redesign PER team — different shape language, layout
// AND font pairing (not just a recolour). Every design reuses the SAME element
// keys (pills / face / line1 / line2 / blurb / footer / badge / watermark) so all
// stay individually toggleable & editable via the Creator panel.

const ink = "#0A0B0D";
const abbr = (id) => TEAM_INFO[id]?.abbr || (id || "").toUpperCase();
const isVert = (o) => o === "v" || o === "slimv";
const aspectFor = (o) => (o === "slimv" ? "aspect-[9/16]" : "aspect-[4/5]");
const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

// Shaped headshot (square / rounded / circle / diamond / hex / arch) — renders
// the image directly so the frame shape is honoured (PlayerAvatar is always round).
function Face({ subject, px = 96, shape = "square", border, bg = "#0E1110", fit = "cover" }) {
    const crop = usePlayerCrop(subject?.id || "");
    if (subject?.kind === "team") return (
        <div className="shrink-0 flex items-center justify-center" style={{ width: px, height: px, background: bg, border }}><TeamBadge teamId={subject.team_id} size="xl" asLink={false} /></div>
    );
    const clip = { diamond: "polygon(50% 0,100% 50%,50% 100%,0 50%)", hex: "polygon(25% 0,75% 0,100% 50%,75% 100%,25% 100%,0 50%)", arch: "path('M0 100 L0 45 A50 45 0 0 1 100 45 L100 100 Z')" }[shape];
    const radius = shape === "circle" ? "9999px" : shape === "rounded" ? "16px" : "0";
    const x = crop?.x ?? 50, y = crop?.y ?? 50, z = crop?.z ?? 1;
    return (
        <div className="shrink-0 overflow-hidden" style={{ width: px, height: px, borderRadius: radius, clipPath: clip, background: bg, border }} data-testid="story-face">
            {subject?.photo_url
                ? <img src={assetUrl(subject.photo_url)} alt={subject.name} className="w-full h-full" style={{ objectFit: fit, objectPosition: `${x}% ${y}%`, transform: z !== 1 ? `scale(${z})` : undefined, transformOrigin: `${x}% ${y}%` }} />
                : <div className="w-full h-full flex items-center justify-center font-story-punch text-white/50" style={{ fontSize: px * 0.34 }}>{initials(subject?.name)}</div>}
        </div>
    );
}

const Frame = ({ o, square, style, className = "", children, team, extra }) => (
    <article data-testid={extra?.testId} data-orientation={o} data-skin="v2" data-team={team}
        className={`group relative overflow-hidden ${square ? "rounded-none" : "rounded-xl"} shadow-xl shadow-black/40 flex flex-col ${isVert(o) ? aspectFor(o) : "h-full min-h-[236px]"} ${className}`}
        style={style}>{children}</article>
);

// Team badge whose size scales PER TEAM via `s._logoScale` (1 = default). Each
// team's contract card stores its own value, so a team with an oversized crest
// can be dialed down without affecting any other team.
function CBadge({ s, cls = "h-7 w-7" }) {
    const sc = s?._logoScale ?? 1;
    return <div className={cls} style={sc !== 1 ? { transform: `scale(${sc})`, transformOrigin: "center" } : undefined}><TeamBadge teamId={s.teamId} size="sm" asLink={false} /></div>;
}

// Compact shared bar for the thin "Slim (H)" format (12 bespoke slim layouts is
// overkill) — keeps the team's shape cue (left block) + colour.
function SlimBar({ s, color, show, o, square, testId, team }) {
    return (
        <Frame o={o} square={square} team={team} extra={{ testId }} className="flex-row items-stretch" style={{ background: "#0A0B0D", border: `1px solid ${color}59` }}>
            <span className="w-1.5 shrink-0" style={{ background: color }} />
            {show("face") && s.subject && <div className="p-2 self-center"><Face subject={s.subject} px={50} shape="square" border={`2px solid ${color}`} /></div>}
            <div className="min-w-0 flex-1 py-2.5 pr-3 flex flex-col justify-center gap-1">
                {show("pills") && <div className="flex gap-1">{(s.pills || []).slice(0, 1).map((p, i) => <span key={i} className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest" style={{ background: color, color: ink }}>{p}</span>)}</div>}
                {show("line2") && <div className="font-story-punch uppercase leading-none text-lg truncate text-white">{s.line2}</div>}
            </div>
        </Frame>
    );
}

const Chips = ({ s, color, show, variant = "solid" }) => show("pills") ? (
    <div className="flex flex-wrap gap-1.5" data-testid="story-pills">
        {(s.pills || []).map((p, i) => {
            const st = variant === "outline" ? { border: `2px solid ${color}`, color } : variant === "underline" ? { color, borderBottom: `2px solid ${color}` } : (i === 0 ? { background: color, color: ink } : { border: `2px solid ${color}`, color });
            return <span key={i} className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-widest leading-none ${variant === "pill" ? "rounded-full" : ""}`} style={st} data-testid="story-type-pill">{p}</span>;
        })}
    </div>
) : null;

const Crest = ({ s, color, show, px = 40, square = true }) => (s.teamId && (s._crest || show("badge"))) ? (
    <div className="shrink-0 flex items-center justify-center" style={{ width: px, height: px, border: `2px solid ${color}`, borderRadius: square ? 0 : "9999px", background: "#0E1110" }} data-testid="story-crest-circle"><TeamBadge teamId={s.teamId} size="sm" asLink={false} /></div>
) : null;

const Blurb = ({ s, color, show, dark = true, className = "" }) => (show("blurb") && s.blurb) ? (
    <p className={`text-[12px] sm:text-[13px] leading-snug line-clamp-3 ${dark ? "text-white/60" : "text-black/60"} ${className}`} data-testid="story-blurb">{s.blurb}</p>
) : null;

const FootBar = ({ s, color, show, testId, dark = false }) => show("footer") ? (
    <div className="flex items-center gap-2 px-4 py-2.5 text-[11px] font-black uppercase tracking-wider mt-auto" style={{ background: color, color: ink }} data-testid={`${testId}-footer`}>
        {s.teamId && <TeamBadge teamId={s.teamId} size="xs" asLink={false} />}
        <span>{abbr(s.teamId)}</span>
        {s.oppId && s.oppId !== s.teamId && <span className="opacity-70">→ {abbr(s.oppId)}</span>}
        <span className="ml-auto tabular-nums opacity-80">{s.ago}</span>
    </div>
) : null;

// ── 1. BOSTON — LEFT SPINE (square face, thick colour spine, punch) ──
function D_bos({ s, color, show, o, square, testId }) {
    return (
        <Frame o={o} square={square} team="bos" extra={{ testId }} className="flex-row" style={{ background: "#0A0B0D", border: `1px solid ${color}59` }}>
            <span className="w-3 shrink-0" style={{ background: color }} />
            <div className="flex flex-col flex-1 min-w-0">
                <div className="relative flex flex-col flex-1 min-h-0 p-5 gap-3">
                    <div className="flex items-start justify-between gap-2"><Chips s={s} color={color} show={show} /><Crest s={s} color={color} show={show} /></div>
                    {show("face") && s.subject && <Face subject={s.subject} px={isVert(o) ? 92 : 108} shape="square" border={`3px solid ${color}`} />}
                    <div className="flex flex-col">
                        {show("line1") && <div className="font-story-serif text-lg text-white/60">{s.line1}</div>}
                        {show("line2") && <div className="font-story-punch uppercase text-3xl sm:text-4xl text-white leading-[0.9] mt-1" style={{ backgroundImage: `linear-gradient(${color},${color})`, backgroundSize: "100% 10px", backgroundPosition: "0 92%", backgroundRepeat: "no-repeat" }}>{s.line2}</div>}
                    </div>
                    <Blurb s={s} color={color} show={show} className="border-l-2 pl-3" />
                </div>
                <FootBar s={s} color={color} show={show} testId={testId} />
            </div>
        </Frame>
    );
}

// ── 2. MONTREAL — TOP BANNER (full colour header, centred circle, serif) ──
function D_mtl({ s, color, show, o, square, testId }) {
    return (
        <Frame o={o} square={square} team="mtl" extra={{ testId }} style={{ background: "#0C0A0B", border: `1px solid ${color}59` }}>
            <div className="px-5 py-3 flex items-center justify-between" style={{ background: color }}>
                <Chips s={s} color={color} show={show} variant="ghost-on-color" />
                {show("pills") && (s.pills || []).map((p, i) => <span key={i} className="text-[11px] font-black uppercase tracking-widest" style={{ color: ink }}>{i === 0 ? p : ""}</span>)}
                {s.teamId && show("badge") && <CBadge s={s} cls="h-7 w-7" />}
            </div>
            <div className="flex-1 flex flex-col items-center text-center justify-center gap-3 px-5 py-4">
                {show("face") && s.subject && <Face subject={s.subject} px={isVert(o) ? 104 : 96} shape="circle" border={`3px solid ${color}`} />}
                {show("line1") && <div className="font-story-serif italic text-lg text-white/60">{s.line1}</div>}
                {show("line2") && <div className="font-story-serif uppercase text-3xl sm:text-4xl text-white leading-[0.95] tracking-tight" style={{ fontWeight: 900 }}>{s.line2}</div>}
                <Blurb s={s} color={color} show={show} className="max-w-[16rem]" />
            </div>
            <FootBar s={s} color={color} show={show} testId={testId} />
        </Frame>
    );
}

// ── 3. TORONTO — SPLIT PANEL (colour half + dark half) ──
function D_tor({ s, color, show, o, square, testId }) {
    return (
        <Frame o={o} square={square} team="tor" extra={{ testId }} className="flex-row" style={{ background: "#0A0B0D", border: `1px solid ${color}59` }}>
            <div className="w-[42%] shrink-0 flex flex-col items-center justify-center gap-3 p-4" style={{ background: color }}>
                {show("face") && s.subject && <Face subject={s.subject} px={isVert(o) ? 86 : 96} shape="circle" border={`3px solid ${ink}`} bg="#0A0B0D" />}
                {show("line1") && <div className="font-heading uppercase text-xs tracking-widest text-center" style={{ color: ink }}>{s.line1}</div>}
                {show("badge") && s.teamId && <CBadge s={s} cls="h-8 w-8" />}
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
                <div className="flex-1 flex flex-col justify-center gap-2.5 p-5">
                    <Chips s={s} color={color} show={show} variant="outline" />
                    {show("line2") && <div className="font-heading font-black uppercase text-3xl sm:text-4xl text-white leading-[0.9]">{s.line2}</div>}
                    <Blurb s={s} color={color} show={show} />
                </div>
                <FootBar s={s} color={color} show={show} testId={testId} />
            </div>
        </Frame>
    );
}

// ── 4. MINNESOTA — TICKET STUB (dashed perforation, mono, side stub) ──
function D_min({ s, color, show, o, square, testId }) {
    return (
        <Frame o={o} square={square} team="min" extra={{ testId }} className="flex-row" style={{ background: "#0B0A0D", border: `2px solid ${color}` }}>
            <div className="flex-1 min-w-0 flex flex-col p-5 gap-3">
                <div className="flex items-center justify-between"><Chips s={s} color={color} show={show} variant="pill" /><Crest s={s} color={color} show={show} square={false} /></div>
                {show("face") && s.subject && <Face subject={s.subject} px={isVert(o) ? 88 : 100} shape="rounded" border={`2px dashed ${color}`} />}
                {show("line1") && <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">{s.line1}</div>}
                {show("line2") && <div className="font-story-punch uppercase text-3xl text-white leading-[0.9]">{s.line2}</div>}
                <Blurb s={s} color={color} show={show} />
            </div>
            <div className="shrink-0 flex flex-col items-center justify-between py-4 px-2 border-l-2 border-dashed" style={{ borderColor: color }}>
                {show("badge") && s.teamId && <CBadge s={s} cls="h-7 w-7" />}
                <div className="font-mono text-[10px] tracking-[0.3em] text-white/50" style={{ writingMode: "vertical-rl" }}>{abbr(s.teamId)} · {s.ago}</div>
                <div className="h-6 w-6 rounded-full" style={{ background: color }} />
            </div>
        </Frame>
    );
}

// ── 5. NEW YORK — DIAGONAL SLASH (giant faint numerals, diamond face) ──
function D_nyc({ s, color, show, o, square, testId }) {
    return (
        <Frame o={o} square={square} team="nyc" extra={{ testId }} style={{ background: "#08090B", border: `1px solid ${color}59` }}>
            <div className="absolute top-0 right-0 h-28 w-28" style={{ background: color, clipPath: "polygon(100% 0,0 0,100% 100%)" }} />
            <div className="pointer-events-none absolute -bottom-6 -left-2 font-story-punch leading-none select-none" style={{ fontSize: 150, color: `${color}14` }}>#</div>
            <div className="relative flex flex-col flex-1 min-h-0 p-5 gap-3">
                <div className="flex items-start gap-3">
                    {show("face") && s.subject && <Face subject={s.subject} px={isVert(o) ? 92 : 104} shape="diamond" />}
                    <div className="pt-1"><Chips s={s} color={color} show={show} variant="outline" /></div>
                </div>
                {show("line1") && <div className="font-heading uppercase tracking-widest text-xs text-white/50">{s.line1}</div>}
                {show("line2") && <div className="font-story-punch uppercase text-4xl text-white leading-[0.85]" style={{ WebkitTextStroke: `1px ${color}` }}>{s.line2}</div>}
                <Blurb s={s} color={color} show={show} />
            </div>
            <FootBar s={s} color={color} show={show} testId={testId} />
        </Frame>
    );
}

// ── 6. OTTAWA — FRAMED (thick border frame, centred, serif caps, hex face) ──
function D_ott({ s, color, show, o, square, testId }) {
    return (
        <Frame o={o} square={square} team="ott" extra={{ testId }} style={{ background: "#0A0708", border: `6px solid ${color}` }}>
            <div className="flex-1 flex flex-col items-center text-center justify-center gap-3 p-5">
                <div className="flex items-center gap-2"><Chips s={s} color={color} show={show} variant="underline" />{show("badge") && s.teamId && <CBadge s={s} cls="h-7 w-7" />}</div>
                {show("face") && s.subject && <Face subject={s.subject} px={isVert(o) ? 104 : 96} shape="hex" border={`3px solid ${color}`} />}
                {show("line1") && <div className="font-story-serif uppercase tracking-[0.25em] text-[11px] text-white/50">{s.line1}</div>}
                {show("line2") && <div className="font-story-serif uppercase text-3xl sm:text-4xl text-white leading-[0.95]" style={{ fontWeight: 900, letterSpacing: "0.02em" }}>{s.line2}</div>}
                <div className="h-0.5 w-12" style={{ background: color }} />
                <Blurb s={s} color={color} show={show} className="max-w-[15rem]" />
            </div>
        </Frame>
    );
}

// ── 7. VANCOUVER — RIGHT SIDEBAR (colour column, heading font) ──
function D_van({ s, color, show, o, square, testId }) {
    return (
        <Frame o={o} square={square} team="van" extra={{ testId }} className="flex-row" style={{ background: "#08090B", border: `1px solid ${color}59` }}>
            <div className="flex-1 min-w-0 flex flex-col justify-center gap-3 p-5">
                <Chips s={s} color={color} show={show} variant="solid" />
                {show("face") && s.subject && <Face subject={s.subject} px={isVert(o) ? 84 : 96} shape="rounded" border={`2px solid ${color}`} />}
                {show("line1") && <div className="font-heading uppercase tracking-widest text-xs text-white/50">{s.line1}</div>}
                {show("line2") && <div className="font-heading font-black uppercase text-3xl text-white leading-[0.9]">{s.line2}</div>}
                <Blurb s={s} color={color} show={show} />
            </div>
            <div className="w-14 shrink-0 flex flex-col items-center justify-between py-4" style={{ background: color }}>
                {show("badge") && s.teamId && <CBadge s={s} cls="h-8 w-8" />}
                <div className="font-heading font-black text-sm tracking-widest" style={{ color: ink, writingMode: "vertical-rl" }}>{abbr(s.teamId)}</div>
                <span className="text-[9px] font-black tabular-nums" style={{ color: ink }}>{s.ago}</span>
            </div>
        </Frame>
    );
}

// ── 8. SEATTLE — STACKED BLOCKS (solid rectangles stacked) ──
function D_sea({ s, color, show, o, square, testId }) {
    return (
        <Frame o={o} square={square} team="sea" extra={{ testId }} className="gap-1.5 p-1.5" style={{ background: "#07090A" }}>
            <div className="flex items-center justify-between px-3 py-2" style={{ background: `${color}22`, border: `1px solid ${color}59` }}>
                <Chips s={s} color={color} show={show} variant="outline" />
                <Crest s={s} color={color} show={show} />
            </div>
            <div className="flex items-center gap-3 px-3 py-2 flex-1 min-h-0" style={{ background: color }}>
                {show("face") && s.subject && <Face subject={s.subject} px={isVert(o) ? 78 : 88} shape="square" border={`3px solid ${ink}`} bg="#0A0B0D" />}
                <div className="min-w-0">
                    {show("line1") && <div className="font-heading uppercase text-[11px] tracking-widest" style={{ color: ink }}>{s.line1}</div>}
                    {show("line2") && <div className="font-heading font-black uppercase text-2xl sm:text-3xl leading-[0.9]" style={{ color: ink }}>{s.line2}</div>}
                </div>
            </div>
            {show("blurb") && s.blurb && <div className="px-3 py-2" style={{ background: "#0E1113" }}><Blurb s={s} color={color} show={show} /></div>}
            {show("footer") && <div className="px-3 py-2 flex items-center justify-between text-[10px] font-black uppercase tracking-wider text-white/50" style={{ background: `${color}22` }}><span>{abbr(s.teamId)}</span><span>{s.ago}</span></div>}
        </Frame>
    );
}

// ── 9. DETROIT — MONO MINIMAL (black + white, thin rules, mono, square outline) ──
function D_det({ s, color, show, o, square, testId }) {
    const c = color === "#000000" ? "#FFFFFF" : color;
    return (
        <Frame o={o} square={square} team="det" extra={{ testId }} style={{ background: "#050505", border: "1px solid rgba(255,255,255,0.18)" }}>
            <div className="flex-1 flex flex-col p-5 gap-3">
                <div className="flex items-center justify-between border-b border-white/15 pb-2">
                    {show("pills") && <div className="flex gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">{(s.pills || []).map((p, i) => <span key={i}>{p}</span>)}</div>}
                    {show("badge") && s.teamId && <CBadge s={s} cls="h-6 w-6 grayscale contrast-125" />}
                </div>
                {show("face") && s.subject && <Face subject={s.subject} px={isVert(o) ? 96 : 104} shape="square" border="1px solid rgba(255,255,255,0.35)" bg="#0A0A0A" fit="cover" />}
                {show("line1") && <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/45">{s.line1}</div>}
                {show("line2") && <div className="font-heading font-black uppercase text-3xl sm:text-4xl text-white leading-[0.9] tracking-tight">{s.line2}</div>}
                <Blurb s={s} color={color} show={show} />
            </div>
            {show("footer") && <div className="flex items-center justify-between px-5 py-2.5 border-t border-white/15 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50"><span>{abbr(s.teamId)}</span><span>{s.ago}</span></div>}
        </Frame>
    );
}

// ── 10. HAMILTON — BOARDING PASS (light cream, dotted, mono ticket) ──
function D_ham({ s, color, show, o, square, testId }) {
    return (
        <Frame o={o} square={square} team="ham" extra={{ testId }} style={{ background: "#F5EFE2", border: `2px solid ${color}` }}>
            <div className="flex items-center justify-between px-4 py-2" style={{ background: color }}>
                {show("pills") && <div className="flex gap-2 font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: ink }}>{(s.pills || []).map((p, i) => <span key={i}>{p}</span>)}</div>}
                {show("badge") && s.teamId && <CBadge s={s} cls="h-6 w-6" />}
            </div>
            <div className="flex-1 flex items-center gap-4 px-4 py-3">
                {show("face") && s.subject && <Face subject={s.subject} px={isVert(o) ? 88 : 96} shape="rounded" border={`2px solid ${color}`} bg="#E9E1D0" />}
                <div className="min-w-0 border-l-2 border-dotted pl-3" style={{ borderColor: color }}>
                    {show("line1") && <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/45">{s.line1}</div>}
                    {show("line2") && <div className="font-story-punch uppercase text-2xl sm:text-3xl text-black leading-[0.9]">{s.line2}</div>}
                    <Blurb s={s} color={color} show={show} dark={false} className="mt-1.5" />
                </div>
            </div>
            {show("footer") && <div className="flex items-center justify-between px-4 py-2 border-t-2 border-dotted font-mono text-[10px] uppercase tracking-[0.2em] text-black/50" style={{ borderColor: color }}><span>{abbr(s.teamId)} → {abbr(s.oppId || s.teamId)}</span><span>{s.ago}</span></div>}
        </Frame>
    );
}

// ── 11. SAN JOSE — MAGAZINE COVER (big serif headline over face) ──
function D_sj({ s, color, show, o, square, testId }) {
    return (
        <Frame o={o} square={square} team="sj" extra={{ testId }} style={{ background: "#0A0708" }}>
            {show("face") && s.subject && <div className="absolute inset-0"><Face subject={s.subject} px={9999} shape="square" bg="#0A0708" fit="cover" /></div>}
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${color}cc 0%, transparent 30%, transparent 55%, #050505f2 100%)` }} />
            <div className="relative flex flex-col flex-1 min-h-0 p-5">
                <div className="flex items-center justify-between"><Chips s={s} color={color} show={show} variant="solid" />{show("badge") && s.teamId && <CBadge s={s} cls="h-8 w-8" />}</div>
                <div className="mt-auto">
                    {show("line1") && <div className="font-story-serif italic text-lg text-white/80">{s.line1}</div>}
                    {show("line2") && <div className="font-story-serif uppercase text-4xl sm:text-5xl text-white leading-[0.85]" style={{ fontWeight: 900, textShadow: "0 2px 20px rgba(0,0,0,0.6)" }}>{s.line2}</div>}
                    <Blurb s={s} color={color} show={show} className="mt-2" />
                    {show("footer") && <div className="mt-3 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-white/60"><span className="px-1.5 py-0.5" style={{ background: color, color: ink }}>{abbr(s.teamId)}</span><span>{s.ago}</span></div>}
                </div>
            </div>
        </Frame>
    );
}

// ── 12. VEGAS — STAMP GRID (perforated squares, postage feel) ──
function D_lvs({ s, color, show, o, square, testId }) {
    return (
        <Frame o={o} square={square} team="lvs" extra={{ testId }} className="p-2.5" style={{ background: "#0A0A06", border: `2px dashed ${color}` }}>
            <div className="flex-1 grid grid-cols-3 grid-rows-3 gap-1.5 min-h-0">
                <div className="col-span-2 row-span-2 flex items-center justify-center" style={{ border: `1px solid ${color}59`, background: "#0E0E08" }}>
                    {show("face") && s.subject && <Face subject={s.subject} px={isVert(o) ? 118 : 104} shape="square" />}
                </div>
                <div className="flex items-center justify-center" style={{ background: color }}>{show("badge") && s.teamId && <CBadge s={s} cls="h-8 w-8" />}</div>
                <div className="flex items-center justify-center p-1" style={{ border: `1px solid ${color}59` }}>{show("pills") && <span className="font-story-punch uppercase text-[10px] text-center leading-tight" style={{ color }}>{(s.pills || [])[0]}</span>}</div>
                <div className="col-span-3 flex flex-col justify-center px-3 py-2" style={{ border: `1px solid ${color}59`, background: "#0E0E08" }}>
                    {show("line1") && <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">{s.line1}</div>}
                    {show("line2") && <div className="font-story-punch uppercase text-2xl sm:text-3xl text-white leading-[0.9]">{s.line2}</div>}
                </div>
            </div>
            {show("blurb") && s.blurb && <div className="pt-2"><Blurb s={s} color={color} show={show} /></div>}
            {show("footer") && <div className="pt-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color }}><span>{abbr(s.teamId)}</span><span>{s.ago}</span></div>}
        </Frame>
    );
}

export const TEAM_V2_DESIGN = { bos: D_bos, mtl: D_mtl, tor: D_tor, min: D_min, nyc: D_nyc, ott: D_ott, van: D_van, sea: D_sea, det: D_det, ham: D_ham, sj: D_sj, lvs: D_lvs };
export const teamHasV2 = (id) => !!TEAM_V2_DESIGN[id];

// Dispatcher — routes to the team's bespoke design (by s.teamId), compact bar
// for the thin Slim (H) format, else the Boston fallback.
export function ContractV2Card({ s, testId, orientation = "v", square = false, hideEls = [] }) {
    const show = (k) => !hideEls.includes(k);
    const team = s.teamId || "bos";
    const color = s.teamColor || teamColor(team);
    if (orientation === "slim") return <SlimBar s={s} color={color} show={show} o={orientation} square={square} testId={testId} team={team} />;
    const Design = TEAM_V2_DESIGN[team] || D_bos;
    return <Design s={s} color={color} show={show} o={orientation} square={square} testId={testId} />;
}
